/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import LogoSeteukPro from "assets/img/logo/logo-seteuk-pro.svg";
import { ReactComponent as LogoSeteukApp } from "assets/img/logo/logo-seteuk.svg";
import { PALETTE, SIZE } from "styles/base/variables";
import Container from "components/common/Container";
import Cookies from "js-cookie";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useEffect, useState } from "react";
import BaseButton from "components/common/Button/Base";
import { getUserInfo, sendLogoutRequest } from "pages/remotes";
import { UserInfo } from "models/user.model";
import UserDialog from "components/common/Dialog/User";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import { MENU_TYPE_LABELS, MENU_TYPES, MenuType } from "models/business.model";

const userMenuButtonCss = css`
  width: 100%;
  padding: 12px 6px;
  display: flex;
  align-items: center;
  gap: 16px;

  &:hover {
    background-color: ${PALETTE.backgroundDisabled};
  }
`;

const navDefaultButtonCss = css`
  height: 100%;
  padding-left: 24px;
  padding-right: 24px;
`;

const navActiveButtonCss = css`
  ${navDefaultButtonCss}
  color: ${PALETTE.primary};
  font-weight: 500;
  border-radius: 0;
  border-top: 2px solid transparent;
  border-bottom: 2px solid ${PALETTE.primary};
`;

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState<boolean>(false);
  const [isUserRefresh, setIsUserRefresh] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    onSetUserInfo();
  }, []);

  useEffect(() => {
    if (isUserRefresh) {
      setIsUserRefresh(false);
      setAlertDialogData({
        message: "유저 정보가 수정되었습니다.",
        type: "success",
      });
      setIsAlertOpen(true);
      onSetUserInfo();
    }
  }, [isUserRefresh]);

  const onSetUserInfo = () => {
    getUserInfo()
      .then((res) => {
        setUserInfo(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const signOut = () => {
    sendLogoutRequest()
      .then((res) => {
        Cookies.remove("X-AUTH-TOKEN");
        Cookies.remove("X-AUTH-TOKEN", { domain: ".aibizon.com" });
        Cookies.remove("ORIGIN", { domain: ".aibizon.com" });
        navigate("/login");
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const openUserDialog = () => {
    setIsUserDialogOpen(true);
    setIsUserMenuOpen(false);
  };

  const goSeteukApp = () => {
    window.open("https://seteuk.aibizon.com");
  };

  const navigatePage = (page: MenuType) => () => {
    const pathname = location.pathname;
    if (pathname.includes(page)) return; // 현재 페이지와 동일한 경우 이동하지 않음
    const paths = pathname.split("/").filter(Boolean);
    if (MENU_TYPES.includes(paths[0] as MenuType)) {
      window.location.href = `/${page}`; // root 페이지면 params 리셋 이동
    } else {
      let destinationPath = paths
        .map((path) => {
          if (MENU_TYPES.includes(path as MenuType)) {
            return page; // card, bank 또는 tax로 변경
          }
          return path; // 다른 경로는 그대로 유지
        })
        .join("/");
      let searchString = "";

      if (searchParams.get("startDate") || searchParams.get("endDate")) {
        // startDate, endDate가 있는 경우
        const startDate = searchParams.get("startDate");
        const endDate = searchParams.get("endDate");
        if (startDate) {
          searchString += `startDate=${startDate}`;
        }
        if (endDate) {
          if (searchString) searchString += "&"; // 이미 startDate가 있으면 & 추가
          searchString += `endDate=${endDate}`;
        }
      }

      // navitage로 이동하면 테이블 갱신되지 않음
      // navigate({
      //   pathname: `/${destinationPath}`,
      //   search: searchString ? `?${searchString}` : "",
      // });

      // window.location.href로 이동하면 테이블 갱신됨
      window.location.href = `/${destinationPath}${
        searchString ? `?${searchString}` : ""
      }`;
    }
  };

  const openDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  const openGuide = () => {
    window.open("https://featpaper.com/v/2PZCXvcC", "_blank");
  };

  return (
    <>
      <header
        css={css`
          background-color: ${PALETTE.light};
          // box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
        `}
      >
        <Container>
          <div
            css={css`
              width: 100%;
              height: ${SIZE.headerHeight}px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <section
              css={css`
                height: 100%;
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <img
                src={LogoSeteukPro}
                alt="Smart CTA"
                width="120px"
                height="fit-content"
              />
              <div
                css={css`
                  height: 100%;
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                {MENU_TYPES.map((menuType) => (
                  <div
                    key={menuType}
                    css={css`
                      position: relative;
                      height: 100%;
                    `}
                  >
                    <BaseButton
                      variant="text"
                      css={
                        location.pathname.includes(`/${menuType}`)
                          ? navActiveButtonCss
                          : navDefaultButtonCss
                      }
                      onClick={navigatePage(menuType)}
                    >
                      {MENU_TYPE_LABELS[menuType]}
                    </BaseButton>
                  </div>
                ))}
              </div>
            </section>

            <section
              css={css`
                height: 100%;
                display: flex;
                align-items: center;
                gap: 12px;
              `}
            >
              <BaseButton
                variant="text"
                onClick={openGuide}
                css={css`
                  margin-right: -8px;
                  color: ${PALETTE.textMid};
                  &:hover {
                    text-decoration: underline;
                    font-weight: 600;
                  }
                `}
              >
                이용가이드
              </BaseButton>
              <div
                css={css`
                  height: 100%;
                  padding: 0 8px;
                  position: relative;
                  display: ${userInfo ? "flex" : "none"};
                  align-items: center;
                  gap: 12px;
                  cursor: pointer;
                `}
                onMouseEnter={() => setIsUserMenuOpen(true)}
                onMouseLeave={() => setIsUserMenuOpen(false)}
              >
                <AccountCircleOutlinedIcon style={{ color: PALETTE.icon }} />
                <div
                  css={css`
                    max-width: 140px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    font-weigth: 500;
                  `}
                >
                  {userInfo?.name || userInfo?.email}
                </div>
                <div
                  css={css`
                    width: 100%;
                    min-width: 140px;
                    padding: 4px 8px;
                    box-sizing: border-box;
                    background-color: ${PALETTE.light};
                    border: 1px solid ${PALETTE.border};
                    border-top: none;
                    border-radius: 0 0 8px 8px;
                    display: ${isUserMenuOpen ? "block" : "none"};
                    position: absolute;
                    top: 100%;
                    left: 0;
                  `}
                >
                  <BaseButton onClick={openUserDialog} css={userMenuButtonCss}>
                    <PersonOutlineOutlinedIcon
                      style={{ color: PALETTE.icon }}
                    />
                    <span
                      css={css`
                        color: ${PALETTE.icon};
                      `}
                    >
                      유저 정보
                    </span>
                  </BaseButton>
                  <div
                    css={css`
                      margin-bottom: 4px;
                      padding-bottom: 4px;
                      border-bottom: 1px solid ${PALETTE.border};
                    `}
                  ></div>
                  <BaseButton onClick={signOut} css={userMenuButtonCss}>
                    <LogoutOutlinedIcon style={{ color: PALETTE.icon }} />
                    <span
                      css={css`
                        color: ${PALETTE.icon};
                      `}
                    >
                      로그아웃
                    </span>
                  </BaseButton>
                </div>
              </div>
              <div>
                <BaseButton
                  variant="outlined"
                  color={PALETTE.primary}
                  onClick={goSeteukApp}
                  css={css`
                    padding: 2px 12px 0;
                    position: relative;

                    &:hover {
                      background-color: ${PALETTE.primaryHover};
                    }
                  `}
                >
                  <LogoSeteukApp
                    css={css`
                      width: 64px;
                      height: fit-content;
                    `}
                  />
                  <ArrowOutwardIcon
                    css={css`
                      width: 14px;
                      position: absolute;
                      top: -2px;
                      right: 2px;
                    `}
                  />
                </BaseButton>
              </div>
            </section>
            <UserDialog
              isOpen={isUserDialogOpen}
              setIsOpen={setIsUserDialogOpen}
              setIsUserRefresh={setIsUserRefresh}
              userInfo={userInfo}
            />
            <AlertDialog
              isOpen={isAlertOpen}
              setIsOpen={setIsAlertOpen}
              alertData={alertDialogData}
            />
          </div>
        </Container>
      </header>
      <div
        css={css`
          height: ${SIZE.headerHeight}px;
        `}
      ></div>
    </>
  );
}

export default Header;
